// <copyright file="add-job-to-purchase-order.ts" company="SoftTech Ltd">
// Copyright (c) SoftTech Ltd. All rights reserved.
// </copyright>

import {
  AskConfirmation,
  ConfirmationButtonType,
  getConfirmationButtons
} from '../../../webmodule-common/other/ui/modal-confirmation';
import { draftPurchaseOrderPicker } from '../../dealer-franchisee/fabrication-purchase-orders/views/draft-purchase-order-picker';
import { fireQuickSuccessToast } from '../../../webmodule-common/other/toast-away';
import { getApiFactory } from '../../api/api-injector';
import { getOnlineSupplierOrWarn } from './supplier-guard';
import { getPurchaseOrderNumberFormatted } from '../../dealer-franchisee/fabrication-purchase-orders/data/fabrication-purchase-order-helper-functions';
import { LangFabricationPurchaseOrder } from '../../../localization/scripts/fabrication-purchase-orders/views/fabrication-purchase-order-lang';

/**
 * Puts a fabrication job on a purchase order: asks for a draft PO, then either adds the job to the
 * one chosen or creates a new PO from it. Shared by every surface that offers the action - the job
 * list's row menu and its Add To badge, and the no-purchase-order prompt on the job detail view -
 * so they cannot drift on what "add to purchase order" does.
 *
 * Returns true only when the job actually ended up on an order. False covers both the user backing
 * out (of the picker or the supplier warning) and the call failing, because callers so far treat
 * those the same: do not carry on with whatever the assignment was a precondition for.
 */
export async function addJobToPurchaseOrder(branchId: string, fabricationJobId: string): Promise<boolean> {
  // A job on two orders is legal - a second PO can cover materials the first missed - but it is
  // more often a slip, so say so before the picker rather than after the damage. Asked here, in
  // the shared flow, so no entry point can forget it.
  const existing = await getApiFactory().fabricationPurchaseOrder().getPurchaseOrdersForJob({ fabricationJobId });
  if (existing && existing.length > 0) {
    const confirmed = await AskConfirmation(
      LangFabricationPurchaseOrder.alreadyOnPoWarning(
        existing.map(po => getPurchaseOrderNumberFormatted(po)).join(', ')
      ),
      getConfirmationButtons()[ConfirmationButtonType.yesNo]
    );
    if (!confirmed) return false;
  }
  const choice = await draftPurchaseOrderPicker(branchId);
  if (!choice) return false;

  const poApi = getApiFactory().fabricationPurchaseOrder();

  if (choice === 'create-new') {
    const supplier = await getOnlineSupplierOrWarn();
    if (!supplier) return false;
    const result = await poApi.createPurchaseOrderFromJobs({
      branchId,
      supplierId: supplier.supplierId,
      title: LangFabricationPurchaseOrder.newPoDefaultTitle(),
      description: '',
      fabricationJobIds: [fabricationJobId]
    });
    if (!result) return false;
    fireQuickSuccessToast(LangFabricationPurchaseOrder.createdPoToast());
    return true;
  }

  const result = await poApi.addJobToPurchaseOrder({
    purchaseOrderId: choice.id,
    recordVersion: choice.recordVersion,
    fabricationJobId
  });
  if (!result) return false;
  fireQuickSuccessToast(LangFabricationPurchaseOrder.addedToPoToast());
  return true;
}
